import React, { useState } from "react";
import { WizardData } from "../index";
import {
  isValidVinFormat,
  isValidVinCheckDigit,
  isCheckDigitRequired,
  formatVin,
} from "../../../services/vinDecoder";
import {
  decodeVinViaServer,
  identityToVinDecodedData,
  pickDisplayTrim,
} from "../../../services/vinApi";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { resolveListingTitle } from "../../../utils";

interface VinEntryStepProps {
  data: WizardData;
  updateData: (updates: Partial<WizardData>) => void;
  onNext: () => void;
  darkMode: boolean;
}

const VinEntryStep: React.FC<VinEntryStepProps> = ({
  data,
  updateData,
  onNext,
  darkMode,
}) => {
  const [vin, setVin] = useState(data.vin || "");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Non-blocking notice (e.g. a non-NA VIN whose ISO 3779 check digit fails but
  // is still worth attempting to decode).
  const [warning, setWarning] = useState<string | null>(null);
  // True once a decode attempt has failed, so we can show a retry CTA + support
  // link instead of a dead end.
  const [decodeFailed, setDecodeFailed] = useState(false);

  const handleVinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = formatVin(e.target.value);
    if (value.length <= 17) {
      setVin(value);
      setError(null);
      setWarning(null);
      setDecodeFailed(false);
    }
  };

  // Single user-facing copy for any case where we can't tie a VIN to a real
  // vehicle. Per plans/nhtsa-normalization-ux.md we never leak the
  // underlying NHTSA error code to the seller — it's almost always either
  // "VIN not in our database" or a transposed character, and the action is
  // the same: re-check and retry.
  const VIN_NOT_FOUND_COPY = "We could not find a vehicle associated with this VIN.";
  const TIMEOUT_COPY =
    "The VIN lookup timed out. Please check your connection and try again.";
  const SUPPORT_EMAIL = "support@p2pcarsales.com";

  const handleDecodeVin = async () => {
    if (!isValidVinFormat(vin)) {
      setError("Please enter a valid 17-character VIN (no I, O, or Q).");
      return;
    }

    // Check-digit policy: hard-block only when ISO 3779 mandates it (North
    // America / China). For other regions a failure is a soft warning so we
    // don't reject legitimate European VINs.
    if (!isValidVinCheckDigit(vin)) {
      if (isCheckDigitRequired(vin)) {
        setError(
          "This VIN's check digit doesn't match. Please double-check the VIN and try again."
        );
        return;
      }
      setWarning(
        "This VIN's check digit doesn't match the North American standard. " +
          "That's common for imported vehicles — we'll still try to decode it."
      );
    }

    setIsLoading(true);
    setError(null);
    setDecodeFailed(false);

    try {
      // Server-backed decode (MarketCheck Basic). Never call a paid provider
      // from the browser — the API key stays on the server, and the route is
      // authenticated + rate-limited.
      const result = await decodeVinViaServer(vin);

      if (result.ok && result.identity?.year && result.identity.make && result.identity.model) {
        const identity = result.identity;
        const vinData = identityToVinDecodedData(identity);
        updateData({
          vin: vin.toUpperCase(),
          vinData,
          year: identity.year,
          make: identity.make,
          model: identity.model,
          trim: pickDisplayTrim(identity) || identity.trim || "",
          bodyStyle: identity.specs?.bodyStyle || "",
          driveType: identity.specs?.driveType || "",
          fuelType: identity.specs?.fuelType || "",
          transmission: identity.specs?.transmission || "",
          engine: identity.specs?.engine || "",
          identityProvider: identity.provider?.name || null,
          identityProviderTier: identity.provider?.tier || null,
          identityStyleKey: identity.styleKey,
          identityVersion: identity.version,
          identityConfidenceRaw: identity.confidenceRaw,
          identityConfidenceScore: identity.confidenceScore,
          identityDisplayTitle: result.displayTitle,
          identitySnapshot: identity,
          enrichmentAttempted: false,
          // Clear stale colors from a previous VIN/enrichment attempt.
          exteriorColor: "",
          interiorColor: "",
          suggestedExteriorColor: null,
          suggestedInteriorColor: null,
          // Clear any stale catalog fields from a previous attempt.
          catalogSourceId: null,
          unmatchedCatalog: false,
          catalogMatchConfidence: null,
          catalogMatchStage: null,
        });
        onNext();
      } else {
        const code = result.ok ? "" : result.errorCode;
        const timedOut = code === "TIMEOUT" || code === "VIN_DECODE_UNAVAILABLE";
        const rateLimited = code === "RATE_LIMITED";
        setError(
          timedOut
            ? TIMEOUT_COPY
            : rateLimited
              ? result.ok
                ? VIN_NOT_FOUND_COPY
                : result.errorMessage
              : VIN_NOT_FOUND_COPY
        );
        setDecodeFailed(true);
      }
    } catch (err) {
      setError(VIN_NOT_FOUND_COPY);
      setDecodeFailed(true);
    } finally {
      setIsLoading(false);
    }
  };

  const vinLength = vin.length;
  const isValidLength = vinLength === 17;

  return (
    <div className="step-container">
      <h2 className="step-title">Enter Your Vehicle's VIN</h2>
      <p className="step-subtitle">
        We'll use this to automatically fill in your vehicle's details
      </p>

      <div className="wizard-form-group">
        <label htmlFor="vin">
          Vehicle Identification Number (VIN)
        </label>
        <input
          type="text"
          id="vin"
          data-testid="wizard-vin-input"
          className="wizard-input vin-input"
          value={vin}
          onChange={handleVinChange}
          placeholder="1HGBH41JXMN109186"
          maxLength={17}
          autoComplete="off"
        />
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          marginTop: "8px",
          fontSize: "12px",
          color: darkMode ? "#888" : "#666"
        }}>
          <span>
            {vinLength}/17 characters
            {isValidLength && (
              <CheckCircleIcon 
                style={{ 
                  fontSize: "14px", 
                  color: "#4caf50", 
                  marginLeft: "4px",
                  verticalAlign: "middle"
                }} 
              />
            )}
          </span>
          <span>
            Usually found on dashboard or driver's door jamb
          </span>
        </div>
      </div>

      {data.vinData && data.vinData.isValid && (
        <div className="vin-decoded-info">
          <div className="vin-decoded-title">
            <CheckCircleIcon style={{ fontSize: "18px" }} />
            Vehicle Identified
          </div>
          <div className="vin-decoded-vehicle">
            {resolveListingTitle({
              identityDisplayTitle: data.identityDisplayTitle,
              year: data.vinData.year,
              make: data.vinData.make,
              model: data.vinData.model,
              trim: data.vinData.trim,
            })}
          </div>
        </div>
      )}

      {warning && !error && (
        <div
          className="vin-warning"
          style={{
            marginTop: "12px",
            padding: "10px 12px",
            borderRadius: "6px",
            fontSize: "13px",
            background: darkMode ? "#3a3320" : "#fff8e1",
            color: darkMode ? "#e0c870" : "#8a6d00",
          }}
        >
          {warning}
        </div>
      )}

      {error && (
        <div className="vin-error">
          {error}
          {decodeFailed && (
            <div style={{ marginTop: "8px", fontSize: "13px" }}>
              Double-check the VIN and try again. Still stuck?{" "}
              <a href={`mailto:${SUPPORT_EMAIL}?subject=VIN%20decode%20help`}>
                Contact support
              </a>
              .
            </div>
          )}
        </div>
      )}

      <div className="wizard-buttons">
        <div /> {/* Spacer */}
        <button
          className="wizard-btn wizard-btn-primary"
          onClick={handleDecodeVin}
          disabled={!isValidLength || isLoading}
        >
          {isLoading ? (
            <span className="wizard-loading">
              <span className="spinner" />
              Decoding VIN...
            </span>
          ) : decodeFailed ? (
            "Try Again"
          ) : (
            "Decode VIN & Continue"
          )}
        </button>
      </div>
    </div>
  );
};

export default VinEntryStep;
